import { getOnboardingDetails, getProductivityDetails } from '../calculations';
import { CHART_COLORS } from '../constants';
import { formatCurrency, formatNumber } from '../formatters';
import { type CurrencyConfig, type CurrencyKey, type RoiResults, type SliderInput } from '../types';
import DataTable, { type DataRow } from './DataTable';
import DonutChart from './DonutChart';
import HorizontalBarChart from './HorizontalBarChart';
import styles from './Report.module.scss';

interface OnboardingSectionProps {
  results: RoiResults;
  inputs: SliderInput;
  currencyConfig: CurrencyConfig;
  currency: CurrencyKey;
}

export default function OnboardingSection({
  results,
  inputs,
  currencyConfig,
  currency,
}: OnboardingSectionProps) {
  const productivity = getProductivityDetails(inputs, currencyConfig);
  const details = getOnboardingDetails(inputs, currencyConfig, productivity);

  const yearsData = [
    { name: 'Year 1', value: results.onboarding.total.year1, color: CHART_COLORS.plum },
    { name: 'Year 2', value: results.onboarding.total.year2, color: CHART_COLORS.plumLight },
    { name: 'Year 3', value: results.onboarding.total.year3, color: CHART_COLORS.orangeDark },
  ];

  const splitData = [
    { name: 'Desktop Workers', value: results.onboarding.desktop.total, color: CHART_COLORS.plum },
    {
      name: 'Frontline Workers',
      value: results.onboarding.frontline.total,
      color: CHART_COLORS.orangeDark,
    },
  ];

  const desktopRows: DataRow[] = [
    {
      label: 'New hires',
      calculation: 'Expected new hires based on growth %',
      values: [...details.desktop.newHires.map(formatNumber), ''],
    },
    {
      label: 'Expected churn',
      calculation: 'Based on current churn rate',
      values: [...details.desktop.expectedChurn.map(formatNumber), ''],
    },
    {
      label: 'Total employees onboarding',
      calculation: 'New hires + expected churn replacements',
      values: [...details.desktop.totalOnboarding.map(formatNumber), ''],
    },
    {
      label: 'Three-month cost for all new hires',
      calculation: 'Onboarding count * hourly rate * hours/month * 3',
      values: [...details.desktop.threeMonthCost.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: 'Lost productivity cost',
      calculation: '50% productivity level in first three months',
      values: [...details.desktop.lostProductivityCost.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: 'Saved productivity value',
      calculation: 'Improvement from intranet (5% desktop improvement)',
      values: [
        ...details.desktop.savedProductivityValue.map((v) => formatCurrency(v, currency)),
        '',
      ],
    },
    {
      label: 'Saving in onboarding productivity',
      calculation: 'Adjusted for unforeseeable business change (35%)',
      values: [
        ...details.desktop.savingInOnboarding.map((v) => formatCurrency(v, currency)),
        formatCurrency(details.desktop.savingTotal, currency),
      ],
    },
  ];

  const frontlineRows: DataRow[] = [
    {
      label: 'New hires',
      calculation: 'Expected new hires based on growth %',
      values: [...details.frontline.newHires.map(formatNumber), ''],
    },
    {
      label: 'Expected churn',
      calculation: 'Based on current churn rate',
      values: [...details.frontline.expectedChurn.map(formatNumber), ''],
    },
    {
      label: 'Total employees onboarding',
      calculation: 'New hires + expected churn replacements',
      values: [...details.frontline.totalOnboarding.map(formatNumber), ''],
    },
    {
      label: 'Three-month cost for all new hires',
      calculation: 'Onboarding count * hourly rate * hours/month * 3',
      values: [...details.frontline.threeMonthCost.map((v) => formatCurrency(v, currency)), ''],
    },
    {
      label: 'Lost productivity cost',
      calculation: '50% productivity level in first three months',
      values: [
        ...details.frontline.lostProductivityCost.map((v) => formatCurrency(v, currency)),
        '',
      ],
    },
    {
      label: 'Saved productivity value',
      calculation: 'Improvement from intranet (2.5% frontline improvement)',
      values: [
        ...details.frontline.savedProductivityValue.map((v) => formatCurrency(v, currency)),
        '',
      ],
    },
    {
      label: 'Saving in onboarding productivity',
      calculation: 'Adjusted for unforeseeable business change (35%)',
      values: [
        ...details.frontline.savingInOnboarding.map((v) => formatCurrency(v, currency)),
        formatCurrency(details.frontline.savingTotal, currency),
      ],
    },
  ];

  const tableHeaders = ['Onboarding', 'Calculation', 'Year 1', 'Year 2', 'Year 3', 'Total'];

  return (
    <section className={styles.section} aria-label="Onboarding Gains">
      <div className={styles.sectionInner}>
        <div className={styles.splitLayout}>
          <div>
            <h2 className={styles.sectionTitle}>Onboarding Gains</h2>
            <p className={styles.bodyText}>
              Onboarding new hires - either in replacement for leavers or when adding new employees
              as part of organic growth - creates a financial burden for every organization.
            </p>
            <p className={styles.bodyText}>
              Some of this cost involves administrative tasks, but a large proportion can be
              attributed to productivity (i.e., &quot;learning the ropes&quot;).
            </p>
            <p className={styles.bodyText}>
              New employees are simply less productive in their first three months than more
              seasoned colleagues. According to{' '}
              <a
                href="https://www.investopedia.com/financial-edge/0711/the-cost-of-hiring-a-new-employee.aspx"
                target="_blank"
                rel="noopener noreferrer"
              >
                Investopedia
              </a>
              , employees are functioning at around 25% productivity in their first month, 50% in
              their second month, and 75% in their third month (or 50% over the first three months).
            </p>
            <p className={styles.bodyText}>
              When it comes to reducing the financial impact of administration and improving new
              starter productivity, a modern intranet can have a compelling impact on the bottom
              line. Preboarding and onboarding experiences orchestrated through a customized
              intranet experience can help new employees complete necessary paperwork, begin getting
              up to speed faster (e.g., understanding the purpose of their role and what&apos;s
              expected of them), meet new teammates, and enjoy (when combined with other
              initiatives) a better employee experience. All of this can also boost early engagement
              and reduce early levels of churn.
            </p>
            <p className={styles.bodyText}>
              It is more important than ever that organizations focus on ensuring that new employees
              have the best possible experience no matter where they work. This focus on creating an
              outstanding employee experience ensures organizations can boost early productivity and
              achieve long-term value from their greatest assets.
            </p>
          </div>
          <div>
            <span className={styles.eyebrow}>Over three years</span>
            <h3 className={styles.sectionValue}>
              {formatCurrency(results.onboarding.total.total, currency)}
            </h3>
            <HorizontalBarChart data={yearsData} currency={currency} height={180} />
            <div className={styles.chartSpacer} />
            <DonutChart data={splitData} currency={currency} />
          </div>
        </div>

        <DataTable
          title="Desktop workers"
          headerIcon="desktop"
          headers={tableHeaders}
          rows={desktopRows}
        />
        <div className={styles.tableSpacer} />
        <DataTable
          title="Frontline workers"
          headerIcon="frontline"
          headers={tableHeaders}
          rows={frontlineRows}
        />
      </div>
    </section>
  );
}
